// Shared "are you sure you want to cut this player?" confirmation, used by both the mobile
// Roster card (GmRoster) and the full player profile (GmPlayer). When the contract is
// guaranteed (i.e. releasing the player leaves dead money on the books), the prompt warns
// that the team keeps paying his salary through the contract's final season. Mirrors the
// worker-side rule in core/player/release.ts: a just-drafted rookie can be cut for free, and
// an already-expired contract owes nothing.

import { PHASE } from "../../common/constants.ts";
import type { Phase, PlayerContract } from "../../common/types.ts";
import { confirm } from "./confirm.tsx";
import { helpers } from "./helpers.ts";

type CutPlayer = {
	draft: { year: number };
	contract: PlayerContract;
};

export const confirmCutPlayer = async (
	p: CutPlayer,
	name: string,
	phase: number,
	season: number,
): Promise<boolean> => {
	const justDrafted = helpers.justDrafted(p, phase as Phase, season);

	// Releasing adds the remaining salary to releasedPlayers unless the contract has expired
	// (or the player was just drafted) — same condition release.ts uses to decide dead money.
	const willOweSalary =
		!justDrafted &&
		(p.contract.exp > season ||
			(p.contract.exp === season && phase < PHASE.PLAYOFFS));

	const message = willOweSalary
		? `${name}'s contract is guaranteed. Cut him and you'll keep paying his ${helpers.formatCurrency(
				p.contract.amount,
				"M",
			)} salary through ${p.contract.exp}. Cut anyway?`
		: `Cut ${name}? He'll become a free agent.`;

	return confirm(message, { okText: "Cut Player", cancelText: "Keep" });
};
